import { A, useNavigate, useParams } from '@solidjs/router';
import { type Component, createEffect, createResource, createSignal, For, Show } from 'solid-js';
import { createSkill, fetchSkill, type Skill, updateSkill } from '../services/api';

const SkillBuilder: Component = () => {
  const params = useParams();
  const navigate = useNavigate();
  const isEditMode = () => !!params.id;

  // Form state - Basic
  const [name, setName] = createSignal('');
  const [description, setDescription] = createSignal('');
  const [version, setVersion] = createSignal('1.0.0');
  const [visibility, setVisibility] = createSignal<Skill['visibility']>('private');

  // Provides section
  const [knowledge, setKnowledge] = createSignal<string[]>([]);
  const [newKnowledge, setNewKnowledge] = createSignal('');
  const [showProvides, setShowProvides] = createSignal(true);

  // Requires section
  const [requiredTools, setRequiredTools] = createSignal<string[]>([]);
  const [newTool, setNewTool] = createSignal('');
  const [requiredSkills, setRequiredSkills] = createSignal<string[]>([]);
  const [newRequiredSkill, setNewRequiredSkill] = createSignal('');
  const [showRequires, setShowRequires] = createSignal(false);

  // Pricing section
  const [pricingType, setPricingType] = createSignal<
    'free' | 'one_time' | 'subscription' | 'per_use'
  >('free');
  const [price, setPrice] = createSignal<number>(0);
  const [showPricing, setShowPricing] = createSignal(false);

  // UI state
  const [isSaving, setIsSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = createSignal(false);

  // Load existing skill if editing
  const [existingSkill] = createResource(
    () => (isEditMode() ? params.id : null),
    async (id) => {
      if (!id) return null;
      return fetchSkill(id);
    },
  );

  // Populate form when skill loads
  createEffect(() => {
    const skill = existingSkill();
    if (skill) {
      setName(skill.name);
      setDescription(skill.description || '');
      setVersion(skill.version);
      setVisibility(skill.visibility);

      // Provides
      if (skill.provides?.knowledge) {
        setKnowledge(skill.provides.knowledge);
      }

      // Requires
      if (skill.requires?.tools) {
        setRequiredTools(skill.requires.tools);
        setShowRequires(true);
      }
      if (skill.requires?.skills) {
        setRequiredSkills(skill.requires.skills);
        setShowRequires(true);
      }

      // Pricing
      if (skill.pricing) {
        setPricingType(skill.pricing.type);
        setPrice(skill.pricing.price || 0);
        setShowPricing(true);
      }

      setHasUnsavedChanges(false);
    }
  });

  // Track changes
  const markChanged = () => setHasUnsavedChanges(true);

  // Knowledge management
  const addKnowledge = () => {
    const item = newKnowledge().trim();
    if (item && !knowledge().includes(item)) {
      setKnowledge([...knowledge(), item]);
      setNewKnowledge('');
      markChanged();
    }
  };

  const removeKnowledge = (index: number) => {
    setKnowledge(knowledge().filter((_, i) => i !== index));
    markChanged();
  };

  // Tool management
  const addTool = () => {
    const item = newTool().trim();
    if (item && !requiredTools().includes(item)) {
      setRequiredTools([...requiredTools(), item]);
      setNewTool('');
      markChanged();
    }
  };

  const removeTool = (index: number) => {
    setRequiredTools(requiredTools().filter((_, i) => i !== index));
    markChanged();
  };

  // Required skill management
  const addRequiredSkill = () => {
    const item = newRequiredSkill().trim();
    if (item && !requiredSkills().includes(item)) {
      setRequiredSkills([...requiredSkills(), item]);
      setNewRequiredSkill('');
      markChanged();
    }
  };

  const removeRequiredSkill = (index: number) => {
    setRequiredSkills(requiredSkills().filter((_, i) => i !== index));
    markChanged();
  };

  const handleSave = async () => {
    if (!name().trim()) {
      setError('Skill name is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const skillData: Partial<Skill> = {
        name: name().trim(),
        description: description().trim() || undefined,
        version: version(),
        visibility: visibility(),
        provides: {
          knowledge: knowledge().length > 0 ? knowledge() : undefined,
        },
        requires:
          requiredTools().length > 0 || requiredSkills().length > 0
            ? {
                tools: requiredTools().length > 0 ? requiredTools() : undefined,
                skills: requiredSkills().length > 0 ? requiredSkills() : undefined,
              }
            : undefined,
        pricing:
          pricingType() !== 'free'
            ? {
                type: pricingType(),
                price: price(),
                currency: 'EUR',
              }
            : undefined,
      };

      if (isEditMode()) {
        await updateSkill(params.id, skillData);
      } else {
        await createSkill(skillData);
      }

      setHasUnsavedChanges(false);
      navigate('/skills');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save skill');
    } finally {
      setIsSaving(false);
    }
  };

  const getCompletionScore = () => {
    let score = 0;
    if (name().trim()) score += 40;
    if (description().trim()) score += 20;
    if (knowledge().length > 0) score += 40;
    return score;
  };

  return (
    <div class="max-w-4xl mx-auto">
      {/* Header */}
      <div class="mb-6">
        <div class="flex items-center gap-4 mb-4">
          <A
            href="/skills"
            class="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m12 19-7-7 7-7" />
              <path d="M19 12H5" />
            </svg>
          </A>
          <div class="flex-1">
            <h1 class="text-2xl font-semibold bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">
              {isEditMode() ? 'Edit Skill' : 'Create Skill'}
            </h1>
            <p class="text-gray-400 text-sm mt-1">
              {isEditMode()
                ? 'Update your skill definition'
                : 'Define reusable domain knowledge for agents'}
            </p>
          </div>
          <Show when={hasUnsavedChanges()}>
            <span class="px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 text-xs">
              Unsaved changes
            </span>
          </Show>
        </div>

        {/* Progress indicator */}
        <div class="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-gray-400">Completion</span>
            <span class="text-sm font-medium text-gray-300">{getCompletionScore()}%</span>
          </div>
          <div class="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              class="h-full bg-gradient-to-r from-green-500 to-teal-500 transition-all duration-300"
              style={{ width: `${getCompletionScore()}%` }}
            />
          </div>
        </div>
      </div>

      {/* Error message */}
      <Show when={error()}>
        <div class="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400">
          {error()}
        </div>
      </Show>

      {/* Loading state for edit mode */}
      <Show when={isEditMode() && existingSkill.loading}>
        <div class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
        </div>
      </Show>

      {/* Form */}
      <Show when={!isEditMode() || !existingSkill.loading}>
        <div class="space-y-6">
          {/* Basic Info Section */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
            <h2 class="text-lg font-medium text-gray-200 mb-4 flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-green-400"
              >
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              Basic Information
            </h2>

            <div class="space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">
                    Skill Name <span class="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={name()}
                    onInput={(e) => {
                      setName(e.currentTarget.value);
                      markChanged();
                    }}
                    placeholder="e.g., Customer Support Knowledge"
                    class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Version</label>
                  <input
                    type="text"
                    value={version()}
                    onInput={(e) => {
                      setVersion(e.currentTarget.value);
                      markChanged();
                    }}
                    placeholder="1.0.0"
                    class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={description()}
                  onInput={(e) => {
                    setDescription(e.currentTarget.value);
                    markChanged();
                  }}
                  placeholder="What does this skill provide? What domain knowledge does it contain?"
                  rows={3}
                  class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors resize-none"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Visibility</label>
                <select
                  value={visibility()}
                  onChange={(e) => {
                    setVisibility(e.currentTarget.value as Skill['visibility']);
                    markChanged();
                  }}
                  class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-green-500 transition-colors"
                >
                  <option value="private">Private - Only you</option>
                  <option value="group">Group - Your team</option>
                  <option value="public">Public - Everyone</option>
                </select>
              </div>
            </div>
          </section>

          {/* Provides Section (Always visible) */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowProvides(!showProvides())}
              class="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
            >
              <div class="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-teal-400"
                >
                  <path d="M20 7h-9" />
                  <path d="M14 17H5" />
                  <circle cx="17" cy="17" r="3" />
                  <circle cx="7" cy="7" r="3" />
                </svg>
                <span class="text-lg font-medium text-gray-200">What This Skill Provides</span>
                <Show when={knowledge().length > 0}>
                  <span class="ml-2 px-2 py-0.5 bg-teal-500/20 text-teal-400 text-xs rounded-full">
                    {knowledge().length} items
                  </span>
                </Show>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class={`text-gray-400 transition-transform ${showProvides() ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <Show when={showProvides()}>
              <div class="px-6 pb-6 space-y-4 border-t border-gray-700 pt-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">
                    Knowledge Items
                  </label>
                  <p class="text-xs text-gray-500 mb-3">
                    Add pieces of domain knowledge that this skill provides to agents.
                  </p>

                  {/* Add knowledge input */}
                  <div class="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newKnowledge()}
                      onInput={(e) => setNewKnowledge(e.currentTarget.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addKnowledge()}
                      placeholder="e.g., How to handle refund requests"
                      class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors text-sm"
                    />
                    <button
                      type="button"
                      onClick={addKnowledge}
                      disabled={!newKnowledge().trim()}
                      class="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  {/* Knowledge list */}
                  <div class="space-y-2">
                    <For each={knowledge()}>
                      {(item, index) => (
                        <div class="flex items-center gap-2 px-3 py-2 bg-gray-900/30 rounded-lg group">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="text-teal-400 flex-shrink-0"
                          >
                            <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
                          </svg>
                          <span class="flex-1 text-sm text-gray-300">{item}</span>
                          <button
                            type="button"
                            onClick={() => removeKnowledge(index())}
                            class="p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </For>
                    <Show when={knowledge().length === 0}>
                      <p class="text-sm text-gray-500 italic py-4 text-center">
                        No knowledge items added yet. Add domain knowledge this skill provides.
                      </p>
                    </Show>
                  </div>
                </div>
              </div>
            </Show>
          </section>

          {/* Requires Section (Collapsible) */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowRequires(!showRequires())}
              class="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
            >
              <div class="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-amber-400"
                >
                  <rect width="20" height="14" x="2" y="5" rx="2" />
                  <line x1="2" x2="22" y1="10" y2="10" />
                </svg>
                <span class="text-lg font-medium text-gray-200">Requirements</span>
                <span class="text-xs text-gray-500">(Optional)</span>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class={`text-gray-400 transition-transform ${showRequires() ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <Show when={showRequires()}>
              <div class="px-6 pb-6 space-y-6 border-t border-gray-700 pt-4">
                {/* Required Tools */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Required Tools</label>
                  <p class="text-xs text-gray-500 mb-3">
                    Tools or integrations this skill requires to function.
                  </p>

                  <div class="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newTool()}
                      onInput={(e) => setNewTool(e.currentTarget.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addTool()}
                      placeholder="e.g., gmail, slack, database"
                      class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors text-sm"
                    />
                    <button
                      type="button"
                      onClick={addTool}
                      disabled={!newTool().trim()}
                      class="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  <div class="flex flex-wrap gap-2">
                    <For each={requiredTools()}>
                      {(tool, index) => (
                        <span class="inline-flex items-center gap-1 px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm">
                          {tool}
                          <button
                            type="button"
                            onClick={() => removeTool(index())}
                            class="p-0.5 hover:bg-amber-500/30 rounded-full"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </span>
                      )}
                    </For>
                  </div>
                </div>

                {/* Required Skills (Dependencies) */}
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">
                    Dependent Skills
                  </label>
                  <p class="text-xs text-gray-500 mb-3">Other skills this skill depends on.</p>

                  <div class="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newRequiredSkill()}
                      onInput={(e) => setNewRequiredSkill(e.currentTarget.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addRequiredSkill()}
                      placeholder="e.g., Basic Customer Service"
                      class="flex-1 px-4 py-2 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors text-sm"
                    />
                    <button
                      type="button"
                      onClick={addRequiredSkill}
                      disabled={!newRequiredSkill().trim()}
                      class="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>

                  <div class="flex flex-wrap gap-2">
                    <For each={requiredSkills()}>
                      {(skill, index) => (
                        <span class="inline-flex items-center gap-1 px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
                          {skill}
                          <button
                            type="button"
                            onClick={() => removeRequiredSkill(index())}
                            class="p-0.5 hover:bg-purple-500/30 rounded-full"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path d="M18 6 6 18" />
                              <path d="m6 6 12 12" />
                            </svg>
                          </button>
                        </span>
                      )}
                    </For>
                  </div>
                </div>
              </div>
            </Show>
          </section>

          {/* Pricing Section (Collapsible) */}
          <section class="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowPricing(!showPricing())}
              class="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
            >
              <div class="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-emerald-400"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
                  <path d="M12 18V6" />
                </svg>
                <span class="text-lg font-medium text-gray-200">Marketplace Pricing</span>
                <span class="text-xs text-gray-500">(Optional)</span>
              </div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class={`text-gray-400 transition-transform ${showPricing() ? 'rotate-180' : ''}`}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            <Show when={showPricing()}>
              <div class="px-6 pb-6 space-y-4 border-t border-gray-700 pt-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-300 mb-2">
                      Pricing Model
                    </label>
                    <select
                      value={pricingType()}
                      onChange={(e) => {
                        setPricingType(
                          e.currentTarget.value as typeof pricingType extends () => infer R
                            ? R
                            : never,
                        );
                        markChanged();
                      }}
                      class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 focus:outline-none focus:border-green-500 transition-colors"
                    >
                      <option value="free">Free</option>
                      <option value="one_time">One-time Purchase</option>
                      <option value="subscription">Subscription</option>
                      <option value="per_use">Pay Per Use</option>
                    </select>
                  </div>

                  <Show when={pricingType() !== 'free'}>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-2">
                        Price (EUR)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={price()}
                        onInput={(e) => {
                          setPrice(Number.parseFloat(e.currentTarget.value) || 0);
                          markChanged();
                        }}
                        placeholder="0.00"
                        class="w-full px-4 py-3 rounded-lg bg-gray-900/50 border border-gray-600 text-gray-200 placeholder-gray-500 focus:outline-none focus:border-green-500 transition-colors"
                      />
                    </div>
                  </Show>
                </div>

                <p class="text-xs text-gray-500">
                  {pricingType() === 'free' && 'This skill will be free for everyone to use.'}
                  {pricingType() === 'one_time' && 'Users pay once for permanent access.'}
                  {pricingType() === 'subscription' &&
                    'Users pay a recurring fee for continued access.'}
                  {pricingType() === 'per_use' && 'Users pay each time the skill is used.'}
                </p>
              </div>
            </Show>
          </section>

          {/* Action Buttons */}
          <div class="flex items-center justify-between pt-4">
            <A
              href="/skills"
              class="px-6 py-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium transition-colors"
            >
              Cancel
            </A>

            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving() || !name().trim()}
              class="px-6 py-3 rounded-lg bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving() ? (
                <span class="flex items-center gap-2">
                  <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Saving...
                </span>
              ) : (
                <span>{isEditMode() ? 'Save Changes' : 'Create Skill'}</span>
              )}
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
};

export default SkillBuilder;
